'use client';

import type { Team } from '@baseball-near-you/prisma';
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@baseball-near-you/ui';
import { useTeams } from '@bb/lib/hooks/useTeams';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { XIcon } from 'lucide-react';
import { TeamSelectionSearch } from './TeamSelectionSearch';

export function DialogTeamSelectionSearch({
  selectedTeam,
  specificTeam,
  search,
  setSearch,
  setSelectedTeam,
  setCantFindTeam,
  onTeamSelect,
  hideCantFindTeam,
  ...props
}: {
  selectedTeamSlug?: string | null;
  specificTeam?: Team | null;
  setSelectedTeam: (team: Team | null) => void;
  selectedTeam?: Team | null;
  search: string | null;
  setSearch: (search: string) => void;
  setCantFindTeam: (cantFindTeam: boolean) => void;
  onTeamSelect: (team: Team) => void;
  hideCantFindTeam?: boolean;
  noResultsNode?: React.ReactNode;
}) {
  const { teams, teamsLoading } = useTeams({ search });
  const { userProfile } = useUserProfile();

  return (
    <>
      <DialogHeader>
        <DialogTitle className="mb-0 text-2xl">
          {props.selectedTeamSlug
            ? `Claim ${specificTeam?.name}`
            : 'Select a team'}
        </DialogTitle>
        <DialogDescription className="mt-2 text-gray-600 text-sm">
          You may claim this profile if you are a coach or owner of this
          organization. Your request will be reviewed by our team.
        </DialogDescription>
      </DialogHeader>
      {!props.selectedTeamSlug && (
        <TeamSelectionSearch
          search={search}
          setSearch={setSearch}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          specificTeam={specificTeam}
          onTeamSelect={onTeamSelect}
          noResultsNode={props.noResultsNode}
        />
      )}
      <DialogFooter>
        <div className="flex w-full flex-col">
          <div
            className={cn(
              'flex w-full flex-col items-center border-t pt-3 sm:flex-row',
              selectedTeam?.slug ? 'justify-between' : 'justify-end'
            )}
          >
            {!!selectedTeam && !props.selectedTeamSlug && (
              <div className="flex w-min max-w-[17rem] gap-x-4 rounded-md border bg-gray-50 pl-3">
                <p className="self-center truncate font-semibold text-base">
                  <span className="hidden md:inline">Selected: </span>
                  {selectedTeam.name}
                </p>
                <Button
                  className="rounded-none border-l bg-transparent p-3 transition-colors hover:bg-gray-100"
                  onClick={() => setSelectedTeam(null)}
                >
                  <XIcon size={20} className="text-gray-600" />
                </Button>
              </div>
            )}
            <Button
              className="mt-4 ml-auto w-full whitespace-nowrap sm:mt-0 sm:w-min"
              onClick={() => {
                if (selectedTeam) {
                  onTeamSelect(selectedTeam);
                }
              }}
              disabled={!selectedTeam?.slug}
            >
              Claim team
            </Button>
          </div>
          {!userProfile?.completedOnboarding && !hideCantFindTeam && (
            <Button
              className="mt-4 w-min self-end whitespace-nowrap"
              variant="outline"
              onClick={() => setCantFindTeam(true)}
            >
              Don't see your team?
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
}
